/*
 * Default TextInput styles
 */
.TextInput {
  display: flex;
  color: var(--brand-control-color-fg-default);
  background-color: var(--brand-color-canvas-default);
  border: none;
  border-radius: var(--brand-borderRadius-medium);
  line-height: var(--brand-text-lineHeight-100);
  transition: var(--brand-control-animation-duration) var(--brand-control-animation-easing);
  transition-property: color, background-color, box-shadow;
}

.TextInput::placeholder {
  color: var(--brand-color-text-muted);
  opacity: 1;
}

.TextInput[disabled] {
  -webkit-text-fill-color: var(--brand-control-color-fg-disabled);
  color: var(--brand-control-color-fg-disabled);
  cursor: not-allowed;
  background-color: var(--brand-control-color-canvas-disabled);
  opacity: 1;
}

.TextInput:focus {
  outline: none;
}

/*
 * Sizes
 */

.TextInput-wrapper--medium {
  height: var(--brand-control-medium-size);
}

.TextInput--medium {
  font-weight: var(--brand-text-weight-100);
  font-size: var(--brand-text-size-100);
  padding: var(--brand-control-medium-paddingBlock-normal) var(--brand-control-medium-paddingInline-condensed);
}

.TextInput--large {
  font-weight: var(--brand-text-weight-200);
  font-size: var(--brand-text-size-200);
  padding: var(--brand-control-large-paddingBlock-normal) var(--brand-control-large-paddingInline-condensed);
}

.TextInput-wrapper--large {
  height: var(--brand-control-large-size);
}

/*
 * Visual states
 */
.TextInput--fullWidth {
  width: 100%;
}

/*
 * TextInput wrapper
 */

.TextInput-wrapper--disabled {
  border-color: var(--brand-control-color-border-disabled);
}

.TextInput-wrapper {
  font-family: var(--brand-body-fontFamily);
  display: inline-flex;
  position: relative;
  border: solid var(--brand-borderWidth-thin, 1px) var(--brand-control-color-border-default);
  border-radius: var(--brand-borderRadius-medium);
  transition: var(--brand-control-animation-duration) var(--brand-control-animation-easing);
  transition-property: border;
}

.TextInput-wrapper--invisible {
  border-color: transparent;
}

.TextInput-wrapper--fullWidth {
  width: 100%;
  display: flex;
}

.TextInput-wrapper:not(.TextInput-wrapper--fullWidth) {
  width: min-content;
}

.TextInput-wrapper--monospace {
  font-family: var(--brand-fontStack-monospace);
}

.TextInput-wrapper:focus-within {
  border-color: var(--brand-control-color-focus);
  box-shadow: 0 0 0 1px var(--brand-control-color-focus);
  outline: none;
}

@media (forced-colors: active) {
  .TextInput-wrapper:focus-within {
    outline: var(--brand-borderWidth-thick) solid Highlight;
    outline-offset: var(--base-size-2);
  }
}

/*
 * Validation
 */

.TextInput-wrapper--success,
.TextInput-wrapper--success.TextInput-wrapper--disabled {
  border-color: var(--brand-color-success-fg);
}

.TextInput-wrapper--error,
.TextInput-wrapper--error.TextInput-wrapper--disabled {
  border-color: var(--brand-color-danger-fg);
}

/*
 * TextInput visuals
 */
.TextInput--has-leading-visual--medium {
  padding-inline-start: calc(
    var(--brand-control-medium-paddingInline-condensed) + var(--base-size-16) + var(--brand-control-medium-gap)
  );
}

.TextInput--has-leading-visual--large {
  padding-inline-start: calc(
    var(--brand-control-large-paddingInline-condensed) + var(--base-size-20) + var(--brand-control-large-gap)
  );
}

.TextInput--has-trailing-visual--medium {
  padding-inline-end: calc(
    var(--brand-control-medium-paddingInline-condensed) + var(--base-size-16) + var(--brand-control-medium-gap)
  );
}

.TextInput--has-trailing-visual--large {
  padding-inline-end: calc(
    var(--brand-control-medium-paddingInline-condensed) + var(--base-size-20) + var(--brand-control-medium-gap)
  );
}

.TextInput-leading-visual,
.TextInput-trailing-visual {
  display: grid;
  position: absolute;
  top: 0;
  display: flex;
  width: var(--base-size-16);
  height: 100%;
  align-items: center;
  color: var(--color-fg-muted);
  pointer-events: none;
  color: var(--brand-color-text-default);
}

.TextInput-leading-visual--medium {
  left: var(--brand-control-medium-paddingInline-condensed);
}

.TextInput-trailing-visual--medium {
  right: var(--brand-control-medium-paddingInline-condensed);
}

.TextInput-leading-visual--large {
  left: var(--brand-control-large-paddingInline-condensed);
}

.TextInput-trailing-visual--large {
  right: var(--brand-control-large-paddingInline-condensed);
}

.TextInput-leading-visual-icon--medium {
  width: var(--base-size-16);
  height: var(--base-size-16);
}

.TextInput-leading-visual-icon--large {
  width: var(--base-size-20);
  height: var(--base-size-20);
}

/*
 * TextInput leading and trailing text
 */

.TextInput-leading-text,
.TextInput-trailing-text {
  display: flex;
  align-items: center;
  color: var(--brand-color-text-muted);
  pointer-events: none;
  white-space: nowrap;
}

.TextInput-leading-text--disabled,
.TextInput-trailing-text--disabled {
  color: var(--brand-control-color-fg-disabled);
  background-color: var(--brand-control-color-canvas-disabled);
}

.TextInput-leading-text {
  border-top-left-radius: var(--brand-borderRadius-medium, 6px);
  border-bottom-left-radius: var(--brand-borderRadius-medium, 6px);
  border-right: 0;
}

.TextInput-trailing-text {
  border-top-right-radius: var(--brand-borderRadius-medium, 6px);
  border-bottom-right-radius: var(--brand-borderRadius-medium, 6px);
  border-left: 0;
}

.TextInput-leading-text-inner,
.TextInput-trailing-text-inner {
  display: inline-flex;
  color: var(--brand-color-text-muted);
}

.TextInput-leading-text-inner--medium,
.TextInput-trailing-text-inner--medium {
  padding-inline: var(--brand-control-medium-paddingInline-condensed);
}

.TextInput-leading-text-inner--large,
.TextInput-trailing-text-inner--large {
  padding-inline: var(--brand-control-large-paddingInline-condensed);
}

.TextInput-leading-text-inner {
  border-right: 1px solid var(--brand-color-border-default);
}

.TextInput-trailing-text-inner {
  border-left: 1px solid var(--brand-color-border-default);
}

.TextInput--has-leading-text {
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.TextInput--has-trailing-text {
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.TextInput-leading-text--success,
.TextInput-trailing-text--success {
  border-color: var(--brand-color-success-fg);
}

.TextInput-leading-text--error,
.TextInput-trailing-text--error {
  border-color: var(--brand-color-danger-fg);
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .TextInput,
  .Checkbox::before,
  .Checkbox::after,
  .Checkbox * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
